import { HttpClient } from '@angular/common/http';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { environment } from 'environments/environment';
import { ThemeService } from '@wind-log_library/fe-component-library';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { DataStudioRedesignService } from 'app/shared/services/data-services/data-studio-redesign/data-studio-redesign.service';
import { DataStudioGranularityType } from 'app/shared/services/data-services/graphql';
import {saveAs} from 'file-saver';
import { TranslateService } from '@ngx-translate/core';
import { BsModalService } from 'ngx-bootstrap/modal';
import { ConfirmationPopupComponent } from 'app/shared/components/confirmation-popup/confirmation-popup.component';
import { DateTimeFormatPreferencePipe } from 'app/shared/pipes/date-time-format-preference/date-time-format-preference.pipe';
import { UserProfileService } from 'app/shared/user-profile/user-profile.service';

@Component({
    selector: 'app-data-files',
    templateUrl: './data-files.component.html',
    styleUrls: ['./data-files.component.scss'],
    standalone: false
})
export class DataFilesComponent implements OnInit, OnDestroy {

  @Input() type;
  public tsFiles = [];
  public dataStudioSubscription;
  public dataStudioURL = environment.dataStudioURL;
  public imageServerURL = environment.image_server;
  public keyToken: any;
  public theme$;
  private unsubscribe$ = new Subject();
  public downloadingFile = '';
  public enumGranularity = DataStudioGranularityType;
  public queryTypes = {
    live: 'DATA_STUDIO.PAGER.REAL_TIME_DATA',
    scada: 'DATA_STUDIO.PAGER.SCADA',
    tlog: 'DATA_STUDIO.PAGER.TURBINE_LOGS',
    metadata: 'DATA_STUDIO.PAGER.METADATA'
  };

  constructor(
              private userProfileService: UserProfileService,
              private http: HttpClient,
              private themeService: ThemeService,
              private translate: TranslateService,
              private modalService: BsModalService,
              private dateTimeFormatPreferencePipe: DateTimeFormatPreferencePipe,
              private dataStudioService: DataStudioRedesignService) {

    this.keyToken = this.userProfileService.getToken();
  }

  private mapNewFileStatus(status: string) {
    switch (status) {
      case "SUCCESSFUL": return 1;
      case "QUEUED": return 2;
      case "IN_PROCESS": return 3;
      case "FAILED": return 4;
      default: return 1;
    }
  }

  ngOnInit() {
    this.themeService.getTheme().pipe(takeUntil(this.unsubscribe$)).subscribe(theme => {
      this.theme$ = theme;
    });
    if (this.type === 'scada' || this.type === 'live') {
      /* NEW QUERY TO GET FILE LIST */
      this.dataStudioSubscription = this.dataStudioService.getFileListNew().subscribe(dataFiles => {
        const files = (dataFiles && dataFiles.length > 0 ? dataFiles : []).map(d => ({
          fileName: d.originalName,
          fileSize: d.fileSize,
          link: d.link,
          state: this.mapNewFileStatus(d.status),
          fileType: d.fileType,
          path: d.fileName,
          _id: d._id,
          requestedAt: d.requestedAt || 0,
          from: d.requestedParams.startTS ? this.dateTimeFormatPreferencePipe.transform(d.requestedParams.startTS, 'dmy') : "",
          to: d.requestedParams.endTS ? this.dateTimeFormatPreferencePipe.transform(d.requestedParams.endTS, 'dmy') : "",
          params: JSON.stringify(d.requestedParams),
          signals: JSON.stringify(d.requestedParams.translatedSignals),
          queryType: d.queryType || 'scada',
        }))
        if (dataFiles) {
          switch (this.type) {
            case 'live':
              this.tsFiles = files.sort((a, b) => b.requestedAt - a.requestedAt).filter(file => file.queryType === 'live');
              break;
            case 'scada':
              this.tsFiles = files.sort((a, b) => b.requestedAt - a.requestedAt).filter(file => file.queryType === 'scada');
              break;
          }
          let totFileSize = 0;
          this.tsFiles.map(row => {
            totFileSize += row.fileSize;
          });
        }
      });
    } else {
      /* OLD QUERY TO GET FILE LIST */
      this.dataStudioSubscription = this.dataStudioService.getFileList().subscribe(dataFiles => {
        if (dataFiles) {
          switch (this.type) {
            case 'tlog':
              this.tsFiles = dataFiles.filter(file => file.queryType === 'tlog');
              break;
            case 'parkdata':
              this.tsFiles = dataFiles.filter(file => file.queryType === 'parkdata');
              break;
            case 'metadata':
              this.tsFiles = dataFiles.filter(file => file.queryType === 'metadata');
              break;
          }
          let totFileSize = 0;
          this.tsFiles.map(row => {
            row.downloadLink = this.dataStudioURL + '/api/v1/files/' + row.path;
            totFileSize += row.fileSize;
          });
          this.tsFiles.reverse();
        }
      });
    }
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next(true);
    this.unsubscribe$.unsubscribe();
    if (this.dataStudioSubscription) { this.dataStudioSubscription.unsubscribe(); }
  }

  public downloadFileName(name) {
    if (name) {
      let newName = '';
      const invalid = ['*', '.', '"', '/', '[', ']', ':', ';', '|', ',', '\\', ' '];
      for (let i = 0; i < name.length; i++) {
        invalid.includes(name.charAt(i)) ? newName += '_' : newName += name.charAt(i);
      }
      return (newName);
    }
  }

  public downloadFile(item: any): void {
    this.downloadingFile = item._id;
    const params = JSON.parse(item.params);
    if (params.queryType === 'scada' || params.queryType === 'live') {
      this.dataStudioService.getDownloadFileNew(item.link).subscribe(response => {
        const dataType: any = response.type;
        const binaryData = [];
        binaryData.push(response);
        const blob = new Blob(binaryData, { type: dataType });
        saveAs(blob, `${item.fileName}`);
        if (response) {
          this.downloadingFile = '';
        }
      }, ( error => {}), () => {
        this.dataStudioService.isDownloading$.next(false);
      });
    } else {
      params.granularity = this.parseGranularity(params.granularity);
      params.isNew = false;
      this.dataStudioService.getDownloadFile(params).subscribe(response => {
        const dataType: any = response.type;
        const binaryData = [];
        binaryData.push(response);
        const blob = new Blob(binaryData, { type: dataType });
        saveAs(blob, `${params.fileName}.${params.fileType}`);
        if (response) {
          this.downloadingFile = '';
        }
      }, ( error => {}), () => {
        this.dataStudioService.isDownloading$.next(false);
      });
    }
  }

  public newDate(date) {
    return date !== null ? date : null;
  }

  public parseParams(params) {
    return JSON.parse(params);
  }

  public deleteFile(item: any) {
    const route = item._id;
    const isScadaOrLiveData = ['scada', 'live'].includes(item.queryType);
    const initialState = { text: this.translate.instant('DATA_STUDIO.DATA_FILES.DELETE_CONFIRMATION')};
    const modalRef = this.modalService.show(ConfirmationPopupComponent,
      { class: `modal-dialog-centered custom-modal-size ${this.themeService.getTheme().value}`, initialState, backdrop: 'static', keyboard: false });
    modalRef.content.onConfirm.pipe(
      takeUntil(this.unsubscribe$), 
    ).subscribe(() => {
      const headers = {Authorization: this.keyToken};
      let url = '';
      if (isScadaOrLiveData) {
        url = this.imageServerURL + `/file/${item.queryType}/`
      } else {
        url = this.dataStudioURL + '/api/v1/files/';
      }
      this.http.delete(url + route, { headers }).subscribe();
      for (let i = 0; i < this.tsFiles.length; i++) {
        if (this.tsFiles[i]._id === route) {
          this.tsFiles.splice(i, 1);
        }
      }
    });
  }

  private parseGranularity(granularity) {
    switch (granularity) {
      case '30s':
        return this.enumGranularity.G30S;
      case '10m':
        return this.enumGranularity.G10M;
      case '1h':
        return this.enumGranularity.G1H;
      case '30d':
        return this.enumGranularity.G1Mon;
      case '1y':
        return this.enumGranularity.G1Y;
    }
  }
}
